// Core React imports
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

// Third-party libraries
import axios from "axios";
import {
  isMobile,
  isChrome,
  isChromium,
  isEdge,
  isEdgeChromium,
} from "react-device-detect";
import { Shield } from "react-feather";

// Application imports
import { SessionMode } from "../libs/constants/index";
import { useBoundStore } from "../store/UseBoundStore1";
import * as Sentry from "../config/sentry";

// Components
import AudioInterviewLogin from "../components/Login/AudioInterviewLogin";
import TelephonicInterview from "../components/Login/TelephonicInterviewLogin";
import VideoInterviewLogin from "../components/Login/VideoInterviewLogin";

// Assets and styles
import logo from "../assests/images/logo.png";
import "../styles/LoginFormNew.css";

const useQuery = () => new URLSearchParams(useLocation().search);

const getBrowserSupport = () => {
  return (
    isChrome || isEdge || isChromium || isEdgeChromium || !!navigator.brave
  );
};
const LoginFormNew = () => {
  const location = useLocation();
  const path = location.pathname;
  const query = useQuery();
  const {
    // Read-only state
    mode,
    isDesktop,
    version,
    firstName,

    // Setters
    setMid,
    setVersion,
    setFirstName,
    setUsername,
    setMode,
    setIsDesktop,
    setJobTitle,
    setOrganizationContactEmail,
    setOrganizationContactNumber,
    setOrganizationContactCountryCode,
    setIs2D,
    setOrganizationName,
  } = useBoundStore();

  const isBrowserSupported = getBrowserSupport();

  // Component state
  const [userData, setUserData] = useState({
    email: "",
    toPhoneNumber: "",
    fromPhoneNumber: "",
    username: "",
    candidateFirstName: "",
    candidateLastName: "",
    candidateJobTitle: "",
  });

  const [interviewData, setInterviewData] = useState({
    isOldUI: "",
    meetingStatus: "",
  });

  const [error, setError] = useState({
    getUserInfoApi: "",
  });

  const [isLoading, setIsLoading] = useState({
    getUserInfoApi: true,
    joinInterviewApi: false,
  });

  useEffect(() => {
    const referenceId = path.startsWith("/l/") ? path.slice(3) : null;

    const isOldUI = query.get("old_ui");
    if (isOldUI) {
      setInterviewData((e) => ({ ...e, isOldUI: true }));
    }

    const is2D = query.get("is_2d") || query.get("is_2D");
    if (is2D) {
      console.log("[LoginFormNew] 2D mode enabled:", {
        is2D,
        queryParam: query.get("is_2d") || query.get("is_2D"),
      });
      setIs2D(true);
    }
    const ver = query.get("version");
    if (["v1", "v2"].includes(ver)) {
      setVersion(ver);
    } else {
      setVersion("v3");
    }
    console.log("[LoginFormNew] Version set:", { version, queryVersion: ver });

    if (referenceId) {
      axios
        // .get(`${process.env.REACT_APP_BASE_URL}/landing/?mId=${mId}`)
        .get(
          `${process.env.REACT_APP_BASE_URL}/landing/?reference=${referenceId}`
        )
        .then((response) => {
          // Check if the response has an error status (expired or not yet available)
          if (response.data.status === "error") {
            setError((e) => ({
              ...e,
              getUserInfoApi: response.data.message,
            }));
            return;
          }

          // Success case - proceed with normal data setting
          setJobTitle(response.data.job_title);
          setFirstName(response.data.first_name);
          setOrganizationName(response.data.organization_name);
          setOrganizationContactEmail(response.data?.contact_email ?? "");
          setOrganizationContactCountryCode(
            response.data?.contact_country_code ?? ""
          );
          setOrganizationContactNumber(response.data?.contact_number ?? "");

          setInterviewData((e) => ({
            ...e,
            meetingStatus: response.data.message,
          }));
          setMid(response.data.meeting_id);
          setUserData({
            email: response.data.email,
            toPhoneNumber: response.data.toPhoneNumber,
            fromPhoneNumber: response.data.fromPhoneNumber,
            username: response.data.username,
            candidateFirstName: response.data.first_name,
            candidateLastName: response.data.last_name,
            candidateJobTitle: response.data.job_title,
          });
          setUsername(response.data.username);
          setMode(response.data.mode);
          if (["v1", "v2"].includes(response.data.version)) {
            setVersion(response.data.version);
          }
        })
        .catch((error) => {
          console.error("[LoginFormNew] Error fetching user info:", {
            referenceId,
            error: error.message,
            status: error.response?.status,
            url: `${process.env.REACT_APP_BASE_URL}/landing/?reference=${referenceId}`,
          });
          if (error.response && error.response.data) {
            setError((e) => ({
              ...e,
              getUserInfoApi: error.response.data.detail,
            }));
          }
          Sentry.captureException(error);
        })
        .finally(() => {
          setIsLoading((e) => ({ ...e, getUserInfoApi: false }));
        });
    } else {
      setIsLoading((e) => ({ ...e, getUserInfoApi: false }));
    }
  }, []);

  useEffect(() => {
    const mediaQueries = {
      phone: window.matchMedia("(max-width: 767px)"),
      tablet: window.matchMedia("(min-width: 768px) and (max-width: 1023px)"),
      desktop: window.matchMedia("(min-width: 1024px)"),
    };

    const updateDeviceType = () => {
      if (mediaQueries.phone.matches) setIsDesktop(false);
      else if (mediaQueries.tablet.matches) setIsDesktop(false);
      else setIsDesktop(true);
    };

    updateDeviceType();
    console.log("[LoginFormNew] Device type detected:", {
      isDesktop,
      isMobile,
      phoneMatches: mediaQueries.phone.matches,
      tabletMatches: mediaQueries.tablet.matches,
      desktopMatches: mediaQueries.desktop.matches,
    });

    Object.values(mediaQueries).forEach((mq) =>
      mq.addEventListener("change", updateDeviceType)
    );

    return () => {
      Object.values(mediaQueries).forEach((mq) =>
        mq.removeEventListener("change", updateDeviceType)
      );
    };
  }, []);
  console.log(
    "error.getUserInfoApi : isLoading.getUserInfoApi : ",
    error.getUserInfoApi,
    isLoading.getUserInfoApi
  );

  return (
    <>
      {isLoading.getUserInfoApi ? (
        <div className="min-h-screen bg-gradient-to-b from-[#f5f7ff] to-[#fafbff] flex justify-center items-center">
          <span className="loading loading-dots loading-sm text-app-primary"></span>
        </div>
      ) : // : !isLoading.getUserInfoApi && error.getUserInfoApi !== "" ? (
      //   <div className="min-h-screen bg-gradient-to-b from-[#f5f7ff] to-[#fafbff] flex justify-center items-center">
      //     <div className="flex flex-col items-center justify-center w-full">
      //       <div className="w-48 md:w-56 mb-5">
      //         <img src={logo} alt="Logo" />
      //       </div>

      //       {!error.getUserInfoApi ? (
      //         <div className="w-full max-w-3xl bg-white rounded-xl shadow-md p-8">
      //           <h1 className="text-2xl font-semibold text-center bg-gradient-to-r from-[#4776e6] to-[#8E54E9] text-transparent bg-clip-text">
      //             Welcome to your interview session
      //             {firstName ? `, ${firstName}!` : ""}
      //           </h1>
      //           <p className="text-center text-xs text-gray-500 mt-2">
      //             Please complete the preparation steps below to ensure the best
      //             interview experience
      //           </p>
      //         </div>
      //       ) : (
      //         <div className="w-full max-w-3xl bg-white rounded-xl shadow-md p-8">
      //           <div className="mt-6 flex items-center justify-center gap-x-3 bg-app-secondaryLight text-app-secondary text-sm px-4 py-3 rounded-md border border-app-secondaryLight">
      //             <Shield size={19} />
      //             <div>
      //               <p className="text-lg font-medium text-app-e">
      //                 {error.getUserInfoApi}
      //               </p>
      //             </div>
      //           </div>
      //         </div>
      //       )}
      //       <p className="text-center text-gray-400 font-normal text-xs mt-4">
      //         © 2025 SofiaWorks. All rights reserved.
      //       </p>
      //     </div>
      //   </div>
      // )
      // !isLoading.getUserInfoApi &&
      // error.getUserInfoApi === "" &&
      (mode === SessionMode.WEB_VIDEO ||
        mode === SessionMode.WEB_VIDEO_WITHOUT_AVATAR) ? (
        <VideoInterviewLogin
          userData={userData}
          interviewData={interviewData}
          LinkError={error.getUserInfoApi}
          isBrowserSupported={isBrowserSupported}
        />
      ) : // !isLoading.getUserInfoApi &&
      // error.getUserInfoApi === "" &&
      mode === SessionMode.WEB_AUDIO ? (
        <AudioInterviewLogin
          userData={userData}
          interviewData={interviewData}
          LinkError={error.getUserInfoApi}
          isBrowserSupported={isBrowserSupported}
        />
      ) : (
        // !isLoading.getUserInfoApi &&
        // error.getUserInfoApi === "" &&
        mode === SessionMode.TELEPHONIC && (
          <TelephonicInterview
            userData={userData}
            interviewData={interviewData}
          />
        )
      )}
    </>
  );
};

export default LoginFormNew;
